import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Trophy, Shield, User, UserCog, Trash2, Crown } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function PlayerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [s, c] = await Promise.all([
        api.get(`/players/${id}/stats`),
        api.get(`/players/counts`),
      ]);
      setData(s.data);
      setCounts(c.data);
    } catch (e) {
      toast.error("Joueur introuvable");
      navigate("/players");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const changeRole = async (newRole) => {
    if (!confirm(`Passer ce joueur en "${newRole}" ?`)) return;
    setSavingRole(true);
    try {
      await api.put(`/players/${id}/role`, { role: newRole });
      toast.success("Rôle mis à jour");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSavingRole(false);
    }
  };

  const deletePlayer = async () => {
    if (!data) return;
    if (!confirm(`Supprimer définitivement ${data.player.name} et toutes ses stats ?`)) return;
    try {
      await api.delete(`/players/${id}`);
      toast.success("Joueur supprimé");
      navigate("/players");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const transferDT = async () => {
    if (!data) return;
    const choice = prompt(
      `Transférer le poste de Directeur technique à ${data.player.name} ?\nTape "coach" pour te rétrograder en coach, "player" pour te rétrograder en joueur.`,
      "coach"
    );
    if (!choice || !["coach", "player"].includes(choice)) return;
    try {
      await api.post(`/players/${id}/transfer-admin`, { fallback_role: choice });
      toast.success("Nouveau Directeur technique nommé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading || !data) {
    return <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>;
  }

  const { player, totals, per_side, motm_count, recent } = data;
  const isCoach = player.role === "coach" || player.role === "admin";
  const ratio =
    totals.matches_played > 0
      ? (totals.goals / totals.matches_played).toFixed(2)
      : "0.00";
  const assistRatio =
    totals.matches_played > 0
      ? (totals.assists / totals.matches_played).toFixed(2)
      : "0.00";

  return (
    <div className="space-y-8">
      <button
        data-testid="back-button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white uppercase tracking-wide font-semibold"
      >
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-[#0B0F17] border border-white/10 p-6 sm:p-8">
        <div className="flex items-center gap-5 flex-wrap">
          <Avatar player={player} size={80} className="ring-2 ring-white/10" />
          <div>
            <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-1">
              {isCoach ? (player.role === "admin" ? "Directeur technique" : "Entraîneur") : player.position || "Joueur"}
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
              {player.name}
            </h1>
          </div>
          {motm_count > 0 && (
            <div
              data-testid="motm-count"
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E5B85C]/15 border border-[#E5B85C]/40"
            >
              <Trophy className="w-4 h-4 text-[#E5B85C]" />
              <span className="font-mono-stat font-bold text-[#E5B85C]">
                {motm_count}× MVP
              </span>
            </div>
          )}
        </div>

        {me?.role === "admin" && me.id !== player.id && (
          <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <UserCog className="w-4 h-4 text-[#E5B85C]" />
              <span className="text-xs font-mono-stat uppercase tracking-widest text-slate-400">
                Gérer le rôle
              </span>
              {counts && (
                <span className="text-[10px] font-mono-stat text-slate-500">
                  · DT {counts.admins}/{counts.max_admins} · entraîneur {counts.coaches}/{counts.max_coaches}
                </span>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { key: "player", label: "Joueur" },
                { key: "coach", label: "Entraîneur" },
                { key: "admin", label: "Directeur technique" },
              ].map((r) => {
                const isCurrent = player.role === r.key;
                let disabled = savingRole || isCurrent;
                if (!isCurrent && counts) {
                  if (r.key === "admin" && counts.admins >= counts.max_admins) disabled = true;
                  if (r.key === "coach" && counts.coaches >= counts.max_coaches) disabled = true;
                }
                return (
                  <button
                    key={r.key}
                    data-testid={`role-btn-${r.key}`}
                    disabled={disabled}
                    onClick={() => changeRole(r.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors ${
                      isCurrent
                        ? "bg-[#E5B85C] text-[#0B0F17]"
                        : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    {r.label}
                  </button>
                );
              })}
              <button
                data-testid="transfer-dt-button"
                onClick={transferDT}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E5B85C]/10 border border-[#E5B85C]/30 text-[#E5B85C] hover:bg-[#E5B85C]/20 text-xs font-bold uppercase tracking-wide"
              >
                <Crown className="w-3.5 h-3.5" /> Transférer DT
              </button>
            </div>
          </div>
        )}

        {(me?.role === "coach" || me?.role === "admin") &&
          me.id !== player.id &&
          player.role !== "admin" && (
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
              <div className="text-xs text-slate-500 font-mono-stat uppercase tracking-widest">
                Suppression joueur
              </div>
              <button
                data-testid="delete-player-button"
                onClick={deletePlayer}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 text-xs font-bold uppercase tracking-wide"
              >
                <Trash2 className="w-3.5 h-3.5" /> Supprimer
              </button>
            </div>
          )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Buts marqués" value={totals.goals} color="#E5B85C" testid="p-goals" />
        <StatCard label="Passes déc." value={totals.assists} color="#60A5FA" testid="p-assists" />
        <StatCard label="Matchs joués" value={totals.matches_played} color="#94A3B8" testid="p-matches" />
        <StatCard label="Ratio buts/match" value={ratio} color="#E5B85C" testid="p-ratio" mono />
        <StatCard label="Ratio passes/match" value={assistRatio} color="#60A5FA" testid="p-ratio-assists" mono />
        <StatCard label="Cartons" value={`${totals.yellow_cards}J / ${totals.red_cards}R`} color="#EAB308" testid="p-cards" small />
      </div>

      {/* Per side */}
      <div>
        <h2 className="font-display font-bold text-xl uppercase mb-3 text-slate-300">
          Par camp
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SideCard label="Les Bleus" side="bleus" data={per_side.bleus} />
          <SideCard label="Les Rouges" side="rouges" data={per_side.rouges} />
        </div>
      </div>

      {/* Recent */}
      <div>
        <h2 className="font-display font-bold text-xl uppercase mb-3 text-slate-300">
          10 derniers matchs
        </h2>
        <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
          {recent.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">
              Aucun match joué pour l'instant
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 border-b border-white/5">
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Camp</th>
                    <th className="p-3 text-right">Buts marqués</th>
                    <th className="p-3 text-right">Assists</th>
                    <th className="p-3 text-right">Cartons</th>
                    <th className="p-3 text-left">Observation</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((s, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-slate-800/50">
                      <td className="p-3 font-mono-stat text-slate-400">
                        <Link
                          to={`/matches/${s.match_id}`}
                          data-testid={`recent-${i}`}
                          className="hover:text-white"
                        >
                          {formatDate(s.match_date)}
                        </Link>
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${
                            s.side === "bleus"
                              ? "bg-sky-500/20 text-sky-300"
                              : "bg-rose-500/20 text-rose-300"
                          }`}
                        >
                          {s.side === "bleus" ? "Bleus" : "Rouges"}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono-stat font-bold text-[#E5B85C]">
                        {s.goals}
                      </td>
                      <td className="p-3 text-right font-mono-stat text-sky-400">{s.assists}</td>
                      <td className="p-3 text-right font-mono-stat text-slate-400">
                        {s.yellow_cards}/{s.red_cards}
                      </td>
                      <td className="p-3 text-slate-400 italic truncate max-w-[220px]">
                        {s.observation || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, testid, mono, small }) {
  return (
    <div
      data-testid={testid}
      className="p-4 rounded-xl bg-slate-900 border border-white/10 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ background: color }} />
      <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div
        className={`font-display font-black mt-2 ${small ? "text-xl" : mono ? "text-3xl font-mono-stat" : "text-4xl"}`}
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

function SideCard({ label, side, data }) {
  const color = side === "bleus" ? "#60A5FA" : "#DC2626";
  return (
    <div
      data-testid={`side-${side}`}
      className="rounded-xl bg-slate-900 border border-white/10 p-5"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="w-3 h-3 rounded-full" style={{ background: color }} />
        <div className="font-display font-bold text-sm uppercase tracking-wide">{label}</div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="text-[10px] font-mono-stat uppercase text-slate-400">Matchs joués</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color }}>
            {data.count}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono-stat uppercase text-slate-400">Buts marqués</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color }}>
            {data.goals}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono-stat uppercase text-slate-400">Assists</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color }}>
            {data.assists}
          </div>
        </div>
      </div>
    </div>
  );
}
